import type { ImpactHit, NotificationView } from './types';

/**
 * Reading notification payloads (statute tracker, docket war room, deadline reminders) and
 * deciding where each one leads. Shared by the bell, the watches page and the alert page so a
 * click goes to the same place from everywhere.
 */

/** The fields a payload may carry. Every field is optional — kinds carry different ones. */
export interface AlertPayload {
  sourceId?: string;
  versionNo?: number;
  citation?: string;
  wordsAdded?: number;
  wordsRemoved?: number;
  matterId?: string;
  impact?: { hits: ImpactHit[]; memo?: string };
  /** An in-app destination some notifications carry (e.g. a Gazette edition ready for review). */
  href?: string;
}

/** The payload as an object; a malformed or non-object payload reads as empty. */
export function parsePayload(json: string | null | undefined): AlertPayload {
  if (!json) return {};
  try {
    const v: unknown = JSON.parse(json);
    if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
    const o = v as Record<string, unknown>;
    const str = (x: unknown) => (typeof x === 'string' && x ? x : undefined);
    const num = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? x : undefined);
    const impact = o.impact as { hits?: unknown; memo?: unknown } | undefined;
    return {
      sourceId: str(o.sourceId),
      versionNo: num(o.versionNo),
      citation: str(o.citation),
      wordsAdded: num(o.wordsAdded),
      wordsRemoved: num(o.wordsRemoved),
      matterId: str(o.matterId),
      href: str(o.href),
      impact: impact && Array.isArray(impact.hits)
        ? { hits: impact.hits as ImpactHit[], memo: str(impact.memo) }
        : undefined,
    };
  } catch {
    return {};
  }
}

/** Whether a notification is a change alert with a comparison behind it (it has a detail page). */
export function isChangeAlert(n: Pick<NotificationView, 'kind' | 'payloadJson'>): boolean {
  if (n.kind !== 'AUTHORITY_CHANGED') return false;
  const p = parsePayload(n.payloadJson);
  return !!p.sourceId && p.versionNo !== undefined;
}

/**
 * Where clicking a notification goes: a change alert opens its detail page; a docket entry or
 * deadline reminder opens its matter; any other notification may carry its own in-app `href`
 * (a Gazette edition ready for review); otherwise there is nowhere better than the list (null).
 */
export function notificationHref(n: Pick<NotificationView, 'id' | 'kind' | 'payloadJson'>): string | null {
  if (isChangeAlert(n)) return `/watches/alerts/${n.id}`;
  const payload = parsePayload(n.payloadJson);
  if (n.kind === 'DOCKET_ENTRY' || n.kind === 'DEADLINE_REMINDER') {
    return payload.matterId ? `/matters/${payload.matterId}` : null;
  }
  // Anything else may name its own destination — honoured only as an in-app path.
  return typeof payload.href === 'string' && /^\/(?!\/)/.test(payload.href) ? payload.href : null;
}

export const HIT_TYPE_LABELS: Record<string, string> = {
  DRAFTER_SESSION: 'Draft',
  GENERATED_DOCUMENT: 'Document',
  FACT_DOCUMENT: 'Case document',
};

/** Deep link to an impacted artifact; case documents live on their matter page. */
export function hitHref(h: ImpactHit): string | null {
  if (h.type === 'DRAFTER_SESSION') return `/drafter/${h.id}`;
  if (h.type === 'GENERATED_DOCUMENT') return `/documents/${h.id}`;
  return h.matterId ? `/matters/${h.matterId}` : null;
}

/** "+14 / −3 words" — or null when the payload has no counts. */
export function wordDelta(p: Pick<AlertPayload, 'wordsAdded' | 'wordsRemoved'>): string | null {
  if (p.wordsAdded === undefined && p.wordsRemoved === undefined) return null;
  return `+${p.wordsAdded ?? 0} / −${p.wordsRemoved ?? 0} words`;
}
